import type { Action, Transfer } from '@0xtorch/core'
import type { Chain } from '../../../chain'
import type { LowerHex } from '../../../types'
import type { TransactionDecoded } from '../../../types/transactionDecoded'
import { createEvmAddressId } from '../../../utils'

/** HyperEVM の chain id */
const hyperEvmChainId = 999

/** HyperCore <-> HyperEVM 間で HYPE を送受信するシステムアドレス */
const hypeSystemAddress: LowerHex = '0x2222222222222222222222222222222222222222'

/**
 * HyperCore <-> HyperEVM 間で HYPE 以外のトークンを送受信するシステムアドレスの形式。
 * `0x20` に続けてトークン番号をビッグエンディアンで埋めたアドレス (例: USDC は `0x2000…0000`)。
 */
const tokenSystemAddressPattern = /^0x200{22}[0-9a-f]{16}$/

/**
 * HyperCore のシステムアドレスかを判定する。
 * @param params.address - 判定するアドレス
 * @returns システムアドレスの場合は true
 */
const isHyperCoreSystemAddress = ({
  address,
}: {
  address: string
}): boolean =>
  address === hypeSystemAddress || tokenSystemAddressPattern.test(address)

/**
 * transfer の相手側 (from / to の account id) が HyperCore のシステムアドレスかを判定する。
 * @param params.accountId - transfer の from または to (`evm_address_<chainId>_<address>`)
 * @returns システムアドレスの場合は true
 */
const isHyperCoreSystemAccountId = ({
  accountId,
}: {
  accountId: string | undefined
}): boolean => {
  if (accountId === undefined) {
    return false
  }
  const prefix = createEvmAddressId({
    chainId: hyperEvmChainId,
    address: '0x',
  })
  if (!accountId.startsWith(prefix)) {
    return false
  }
  return isHyperCoreSystemAddress({
    address: `0x${accountId.slice(prefix.length)}`,
  })
}

type CreateActionsByHyperCoreAnalyzerParameters = {
  readonly chain: Chain
  readonly transaction: TransactionDecoded
  readonly transfers: readonly Transfer[]
  readonly source: string
}

/**
 * HyperEVM と HyperCore (Hyperliquid 取引所) の間の資産移動を解析する。
 * - HyperEVM -> HyperCore: システムアドレスへの送金を send-to-cex とする
 * - HyperCore -> HyperEVM: システムアドレスからの受取を receive-from-cex とする
 *
 * @param parameters.chain - 解析対象チェーン
 * @param parameters.transaction - デコード済み EVM transaction
 * @param parameters.transfers - transaction から抽出した transfer
 * @param parameters.source - action の参照元ID
 * @returns HyperCore との資産移動だけで構成される場合は action、それ以外は undefined
 */
export const createActionsByHyperCoreAnalyzer = ({
  chain,
  transaction,
  transfers,
  source,
}: CreateActionsByHyperCoreAnalyzerParameters):
  | readonly Action[]
  | undefined => {
  if (chain.id !== hyperEvmChainId) {
    return undefined
  }

  const actionTransfers = transfers.filter(
    (transfer) => transfer.direction !== 'none',
  )
  if (actionTransfers.length === 0) {
    return undefined
  }

  const isSendToHyperCore = actionTransfers.every(
    (transfer) =>
      transfer.direction === 'out' &&
      isHyperCoreSystemAccountId({ accountId: transfer.to }),
  )
  const isReceiveFromHyperCore = actionTransfers.every(
    (transfer) =>
      transfer.direction === 'in' &&
      isHyperCoreSystemAccountId({ accountId: transfer.from }),
  )
  if (!isSendToHyperCore && !isReceiveFromHyperCore) {
    return undefined
  }

  return [
    {
      app: transaction.to?.app ?? transaction.from.app,
      comment: isSendToHyperCore
        ? 'HyperEVM -> HyperCore'
        : 'HyperCore -> HyperEVM',
      evidence: 'contract',
      order: 0,
      source,
      timestamp: transaction.timestamp,
      transfers: actionTransfers,
      type: isSendToHyperCore ? 'send-to-cex' : 'receive-from-cex',
    },
  ]
}
